import { useContext } from 'react'
import PublicReportContext from '../../../../../StateManagment/PublicReportContext'
import useSettings from '../../../../../hooks/Settings'
import { TemplateDataContext } from '../../context/TemplateDataContext'
import isTemplateMode from './isTemplateMode'
import useWidgetPersona from './useWidgetPersona'

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T12)
// Nodig omdat de deelnemerwidgets op de openbare link twee dingen anders
// moeten doen dan in de app, en dat niet elk zelf mogen uitzoeken:
// - de gegevens komen uit `personaData` van getPublicLiveReport2, niet uit
//   Firestore (ADR-022, stijlregel 13);
// - de modulecheck kan daar niet: niemand is ingelogd, dus useSettings kent
//   de modules van de eigenaar niet en zou altijd 'module uit' tonen. De
//   bouwer kon de widget alleen met de module aan kiezen, en de server stuurt
//   alleen gegevens mee van een rapport met deelnemer, achter een code.
//
// AI-CONTEXT: See docs/features/livereport-templates-deelnemer.md (T3)
// In een template is het precies zo: de gegevens van de voorbeelddeelnemer
// komen van de server (exportTemplateData → `data.persona`), in dezelfde vorm
// als op de link. `isPublic` betekent dus 'gegevens van de server'.
// De deelnemer van het template geldt ALLEEN voor de deelnemerwidgets: Effect,
// Vraag, Onderzoek enz. lezen useWidgetPersona, dat in een template null
// blijft, zodat de jaarverslagen over de hele Stichting Template blijven gaan.

/**
 * @typedef {'loading' | 'missing' | 'ready'} TemplatePersonaStatus
 */

/**
 * @typedef {object} PublicPersona
 * @property {boolean} isPublic - Gegevens van de server: op de openbare link (LiveReportOpen2) of in een template
 * @property {import('../../../../../StateManagment/PublicReportContext').PublicPersonaData|null} personaData - Alleen van de server
 * @property {string|null} personaId - Over wie de widget gaat: in een template de voorbeelddeelnemer, anders useWidgetPersona
 * @property {boolean} personasModuleOn - Mag de widget iets tonen
 * @property {TemplatePersonaStatus|null} templateStatus - Alleen in een template; `null` daarbuiten
 */

/**
 * @param {{ templateData: object|null, isLoading: boolean, error: Error|null }} template
 * @returns {{ status: TemplatePersonaStatus, personaData: object|null }}
 */
const templatePersona = ({ templateData, isLoading, error }) => {
    if (isLoading || (!templateData && !error)) return { status: 'loading', personaData: null }
    const personaData = templateData?.data?.persona ?? null
    // Oude functie live, gatherer gefaald of deelnemer weg: zeggen, niet 'kies een deelnemer'.
    if (!personaData?.persona?.ID) return { status: 'missing', personaData: null }
    return { status: 'ready', personaData }
}

/** @returns {PublicPersona} */
const usePublicPersona = () => {
    const publicReport = useContext(PublicReportContext)
    const template = useContext(TemplateDataContext)
    const { personasOn } = useSettings()
    const reportPersonaId = useWidgetPersona()

    const isPublic = Boolean(publicReport)
    if (!isPublic && (template.isTemplate || isTemplateMode())) {
        const { status, personaData } = templatePersona(template)
        return {
            isPublic: true,
            personaData,
            personaId: personaData?.persona?.ID ?? null,
            // De bouwer van een template heeft de module zelf niet nodig; de
            // templatelijst toont deelnemertemplates alleen wie hem wél heeft.
            personasModuleOn: true,
            templateStatus: status,
        }
    }

    return {
        isPublic,
        personaData: isPublic ? publicReport.personaData ?? null : null,
        personaId: reportPersonaId,
        personasModuleOn: isPublic || personasOn === 'flex',
        templateStatus: null,
    }
}

export default usePublicPersona
